import React from 'react';
import './OfficeInfo.css';

const PrivacyPolicy = () => {
    return (
        <div className="office-policy-section">
            <div className="section-header">
                <span>Data Protection & Privacy</span>
                <h2>Privacy Policy</h2>
            </div>

            <div className="office-policy-container">
                <div className="policy-details">
                    <div className="policy-section">
                        <p className="policy-intro">
                            Atlantic Primary Care is committed to protecting your privacy and maintaining the confidentiality of your personal information. 
                            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website.
                        </p>
                        <p className="policy-date"><strong>Effective Date:</strong> {new Date().toLocaleDateString()}</p>
                        <p className="policy-date"><strong>Last Updated:</strong> {new Date().toLocaleDateString()}</p>
                    </div>

                    <div className="policy-section">
                        <h4>Information We Collect</h4>
                        
                        <h5>Automatically Collected Information</h5>
                        <p>When you visit our website, we automatically collect certain information through Google Firebase Hosting:</p>
                        <ul>
                            <li><strong>IP address</strong></li>
                            <li><strong>Browser type and version</strong></li>
                            <li><strong>Device type and operating system</strong></li>
                            <li><strong>Pages visited and time spent on pages</strong></li>
                            <li><strong>Referring website addresses</strong></li>
                            <li><strong>Date and time of visits</strong></li>
                        </ul>
                        
                        <h5>Contact Information</h5>
                        <p>If you contact us through our website or by phone, we may collect:</p>
                        <ul>
                            <li><strong>Name and contact information</strong></li>
                            <li><strong>Messages or inquiries you send to us</strong></li>
                            <li><strong>Phone numbers for appointment scheduling</strong></li>
                        </ul>

                        <h5>Health Information</h5>
                        <p>As a healthcare provider, when you become a patient, we collect and maintain protected health information (PHI) as required for medical care, including:</p>
                        <ul>
                            <li><strong>Medical history and records</strong></li>
                            <li><strong>Treatment information</strong></li>
                            <li><strong>Insurance and billing information</strong></li>
                            <li><strong>Appointment and scheduling data</strong></li>
                        </ul>
                    </div>

                    <div className="policy-section">
                        <h4>How We Use Your Information</h4>
                        <p>We use collected information for the following purposes:</p>
                        <ul>
                            <li><strong>Healthcare Services:</strong> Providing medical care, treatment, and related healthcare operations</li>
                            <li><strong>Communication:</strong> Responding to inquiries and healthcare communications</li>
                            <li><strong>Website Functionality:</strong> Maintaining and improving our website performance and user experience</li>
                            <li><strong>Legal Compliance:</strong> Meeting regulatory requirements and legal obligations</li>
                        </ul>
                    </div>

                    <div className="policy-section">
                        <h4>Google Firebase Hosting</h4>
                        <p>Our website is hosted on Google Firebase, a service provided by Google LLC. Firebase automatically collects certain information including:</p>
                        <ul>
                            <li><strong>Server logs with IP addresses and browser information</strong></li>
                            <li><strong>Basic usage analytics and performance metrics</strong></li>
                            <li><strong>Error logs for website functionality</strong></li>
                        </ul>
                        <p>
                            This data is processed in accordance with <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a> and <a href="https://firebase.google.com/terms" target="_blank" rel="noopener noreferrer">Firebase Terms of Service</a>. 
                            Firebase provides security measures including SSL encryption and secure data centers.
                        </p>
                    </div>

                    <div className="policy-section">
                        <h4>Google Analytics Data Collection</h4>
                        <p>
                            Our website uses Google Analytics, a web analytics service provided by Google LLC ("Google"), to help us understand how visitors use our website and improve our services. Google Analytics collects information through Firebase Analytics integration.
                        </p>
                        
                        <h5>Information Collected by Google Analytics</h5>
                        <p>Google Analytics automatically collects the following types of information when you visit our website:</p>
                        <ul>
                            <li><strong>User and Session Data:</strong> Unique user identifiers, session duration, and frequency of visits</li>
                            <li><strong>Device Information:</strong> Device type, operating system, browser type and version, screen resolution, and language settings</li>
                            <li><strong>Geographic Data:</strong> Approximate geographic location based on IP address (city and region level, not precise location)</li>
                            <li><strong>Website Interaction Data:</strong> Pages viewed, time spent on each page, bounce rate, and navigation paths</li>
                            <li><strong>Traffic Source Information:</strong> How users arrived at our website (direct, search engines, social media, referral sites)</li>
                            <li><strong>Technical Data:</strong> Internet service provider, network connectivity, and website performance metrics</li>
                            <li><strong>Engagement Metrics:</strong> Click tracking, scroll depth, file downloads, and form interactions (non-personal data only)</li>
                            <li><strong>Custom Events:</strong> User interactions such as button clicks, video plays, and navigation menu usage</li>
                        </ul>

                        <h5>How Google Analytics Data is Used</h5>
                        <p>We use Google Analytics data to:</p>
                        <ul>
                            <li><strong>Website Performance:</strong> Monitor and improve website functionality and user experience</li>
                            <li><strong>Content Optimization:</strong> Understand which content is most valuable to our patients</li>
                            <li><strong>Technical Improvements:</strong> Identify and resolve technical issues and loading problems</li>
                            <li><strong>Security Monitoring:</strong> Detect potential security threats and unusual website activity</li>
                            <li><strong>Accessibility Enhancements:</strong> Ensure our website works well across different devices and browsers</li>
                        </ul>

                        <h5>Data Processing and Storage</h5>
                        <p>
                            Google Analytics data is processed according to <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a> and <a href="https://marketingplatform.google.com/about/analytics/terms/us/" target="_blank" rel="noopener noreferrer">Google Analytics Terms of Service</a>. 
                            This data may be stored and processed in the United States and other countries where Google maintains facilities.
                        </p>
                        <p>
                            <strong>Important:</strong> Google Analytics on our website does not collect personally identifiable health information or merge analytics data with any health records. All data is anonymized and aggregated for statistical purposes only.
                        </p>

                        <h5>Data Retention</h5>
                        <p>
                            Google Analytics data is automatically deleted after 26 months. You can learn more about Google's data retention practices in their 
                            <a href="https://support.google.com/analytics/answer/7667196" target="_blank" rel="noopener noreferrer">Data Retention documentation</a>.
                        </p>

                        <h5>Opting Out of Google Analytics</h5>
                        <p>You can opt out of Google Analytics tracking by:</p>
                        <ul>
                            <li><strong>Browser Settings:</strong> Disabling cookies or JavaScript in your browser settings</li>
                            <li><strong>Google Analytics Opt-out:</strong> Installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a></li>
                            <li><strong>Do Not Track:</strong> Enabling "Do Not Track" settings in your browser (where supported)</li>
                        </ul>
                        <p>Note that opting out of analytics may affect some website functionality and our ability to improve your user experience.</p>
                    </div>



                    <div className="policy-section">
                        <h4>HIPAA Compliance and Protected Health Information</h4>
                        <p>
                            Atlantic Primary Care is fully committed to <a href="https://www.hhs.gov/hipaa/index.html" target="_blank" rel="noopener noreferrer">HIPAA</a> compliance. We maintain strict safeguards to protect your Protected Health Information (PHI):
                        </p>
                        <ul>
                            <li><strong>Administrative Safeguards:</strong> Staff training, access controls, and administrative oversight</li>
                            <li><strong>Physical Safeguards:</strong> Secure facilities, workstation controls, and media protection</li>
                            <li><strong>Technical Safeguards:</strong> Encryption, secure transmission, and access logging</li>
                        </ul>
                        <p>
                            Your health information is only accessed by authorized personnel for treatment, payment, and healthcare operations. 
                            We do not sell, rent, or share your PHI with third parties except as permitted by HIPAA.
                        </p>
                        <p>
                            <strong>Important:</strong> This website does not collect or store health information. Health information is only collected 
                            during in-person visits or through our separate, HIPAA-compliant patient portal systems.
                        </p>
                    </div>

                    <div className="policy-section">
                        <h4>Third-Party Services</h4>
                        <p>Our website links to the following external services:</p>
                        <ul>
                            <li><strong>Patient Portal:</strong> <a href="https://www.questdiagnostics.com/" target="_blank" rel="noopener noreferrer">Quest Diagnostics patient portal</a> (external link only) - <a href="https://www.questdiagnostics.com/home/patients/privacy-practices" target="_blank" rel="noopener noreferrer">Quest Privacy Policy</a></li>
                            <li><strong>Google Maps:</strong> For location information and directions - <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Maps Privacy Policy</a></li>
                            <li><strong>Phone Services:</strong> Direct calling functionality through your device</li>
                        </ul>
                        <p>These third-party services have their own privacy policies. We are not responsible for their privacy practices.</p>
                    </div>

                    <div className="policy-section">
                        <h4>Data Security</h4>
                        <p>We implement appropriate security measures to protect your information:</p>
                        <ul>
                            <li><strong>SSL/TLS encryption for all data transmission</strong></li>
                            <li><strong>Secure hosting infrastructure through Google Firebase</strong></li>
                            <li><strong>Limited access controls</strong></li>
                            <li><strong>Regular security assessments</strong></li>
                        </ul>
                        <p>However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.</p>
                    </div>

                    <div className="policy-section">
                        <h4>Cookies and Tracking</h4>
                        <p>We may use cookies and similar technologies to:</p>
                        <ul>
                            <li><strong>Improve website functionality</strong></li>
                            <li><strong>Analyze website usage patterns</strong></li>
                            <li><strong>Remember user preferences</strong></li>
                        </ul>
                        <p>You can control cookie settings through your browser. Disabling cookies may affect website functionality.</p>
                    </div>

                    <div className="policy-section">
                        <h4>Your Rights</h4>
                        <p>You have the right to:</p>
                        <ul>
                            <li><strong>Know what personal information we collect</strong></li>
                            <li><strong>Request access to your personal information</strong></li>
                            <li><strong>Request correction of inaccurate information</strong></li>
                            <li><strong>Request deletion of your information (subject to legal requirements)</strong></li>
                        </ul>
                        <p>For health information rights under <a href="https://www.hhs.gov/hipaa/for-individuals/index.html" target="_blank" rel="noopener noreferrer">HIPAA</a>, please refer to our Notice of Privacy Practices provided during your visit.</p>
                    </div>

                    <div className="policy-section">
                        <h4>Data Retention</h4>
                        <p>We retain information as follows:</p>
                        <ul>
                            <li><strong>Website Usage Data:</strong> As long as necessary for website operation and legal compliance</li>
                            <li><strong>Contact Information:</strong> Until you request deletion or as required by law</li>
                            <li><strong>Health Records:</strong> As required by federal and state law (maintained separately from this website)</li>
                        </ul>
                    </div>

                    <div className="policy-section">
                        <h4>Children's Privacy</h4>
                        <p>
                            Our website is not directed to children under 13. We do not knowingly collect personal information from children under 13. 
                            If you believe we have collected information from a child under 13, please contact us immediately.
                        </p>
                    </div>

                    <div className="policy-section">
                        <h4>Changes to This Privacy Policy</h4>
                        <p>
                            We may update this Privacy Policy to reflect changes in our practices, new services or features, or legal requirements. 
                            We will post the updated policy on our website with a new effective date. 
                        </p>
                        <p>
                            For significant changes that affect how we collect, use, or share your information, we will provide additional notice via email or website notification before the changes take effect.
                        </p>
                        <p>
                            Your continued use of our website after changes constitutes acceptance of the updated policy.
                        </p>
                    </div>

                    <div className="policy-section">
                        <h4>Contact Information</h4>
                        <p>If you have questions about this Privacy Policy, please contact us:</p>
                        <div className="contact-info">
                            <p><strong>Atlantic Primary Care</strong></p>
                            <p>Phone: (781) 871-5030</p>
                            <p>Fax: (781) 871-5480</p>
                            <p>
                                <strong>Hanover Office:</strong><br />
                                109 Webster St Suite 9<br />
                                Hanover, MA 02339
                            </p>
                            <p>
                                {/* <strong>Weymouth Office:</strong><br /> */}
                                {/* 851 Main St Suite 25<br /> */}
                                {/* Weymouth, MA 02190 */}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;